// File: src/utils/crypto.js
// Utility functions for encrypting/decrypting sensitive data in browser storage

// Cache for derived keys to avoid redundant PBKDF2 operations
let keyCache = null;
let keyCachePassword = null;

/**
 * Get or initialize the session encryption key
 * This key is generated once per session and reused to prevent infinite re-renders
 * @returns {string} - The session key as hex string
 */
const getOrCreateSessionKey = () => {
    let sessionKeyStr = sessionStorage.getItem('_sk');

    if (!sessionKeyStr) {
        // Generate new session key only if it doesn't exist
        const sessionKey = window.crypto.getRandomValues(new Uint8Array(32));
        sessionKeyStr = Array.from(sessionKey).map(b => b.toString(16).padStart(2, '0')).join('');
        sessionStorage.setItem('_sk', sessionKeyStr);
    }

    return sessionKeyStr;
};

/**
 * Generate a cryptographic key from a password with caching
 * @param {string} password - The password to derive key from
 * @returns {Promise<CryptoKey>} - The derived key
 */
const getKey = async (password) => {
    // Return cached key if password matches
    if (keyCache && keyCachePassword === password) {
        return keyCache;
    }

    try {
        const encoder = new TextEncoder();
        const keyMaterial = await window.crypto.subtle.importKey(
            'raw',
            encoder.encode(password),
            { name: 'PBKDF2' },
            false,
            ['deriveBits', 'deriveKey']
        );

        const derivedKey = await window.crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                salt: encoder.encode('atmospole-salt-2026'), // Static salt for session
                iterations: 10000, // Reduced from 100000 for better performance (still secure)
                hash: 'SHA-256'
            },
            keyMaterial,
            { name: 'AES-GCM', length: 256 },
            false,
            ['encrypt', 'decrypt']
        );

        // Cache the key
        keyCache = derivedKey;
        keyCachePassword = password;

        return derivedKey;
    } catch (error) {
        console.error('Key derivation error:', error);
        throw new Error('Failed to derive encryption key');
    }
};

/**
 * Encrypt a value using AES-GCM
 * @param {string} value - The value to encrypt
 * @returns {Promise<string>} - Base64 encoded encrypted data with IV
 */
export const encryptValue = async (value) => {
    try {
        if (!value) {
            throw new Error('Cannot encrypt empty value');
        }

        // Get or create session key (reuses existing key instead of generating new one)
        const sessionKeyStr = getOrCreateSessionKey();
        const key = await getKey(sessionKeyStr);

        const encoder = new TextEncoder();
        const iv = window.crypto.getRandomValues(new Uint8Array(12));

        const encryptedData = await window.crypto.subtle.encrypt(
            { name: 'AES-GCM', iv },
            key,
            encoder.encode(value)
        );

        // Combine IV + encrypted data and convert to base64
        const combined = new Uint8Array(iv.length + encryptedData.byteLength);
        combined.set(iv, 0);
        combined.set(new Uint8Array(encryptedData), iv.length);

        const base64 = btoa(String.fromCharCode(...combined));
        return base64;
    } catch (error) {
        console.error('Encryption error:', error);
        throw new Error('Failed to encrypt data: ' + error.message);
    }
};

/**
 * Decrypt a value using AES-GCM
 * @param {string} encryptedValue - Base64 encoded encrypted data with IV
 * @returns {Promise<string>} - The decrypted value
 */
export const decryptValue = async (encryptedValue) => {
    try {
        if (!encryptedValue) {
            throw new Error('Cannot decrypt empty value');
        }

        const sessionKeyStr = sessionStorage.getItem('_sk');
        if (!sessionKeyStr) {
            throw new Error('Session key not found. Session may have expired.');
        }

        const key = await getKey(sessionKeyStr);

        // Decode base64 and split IV + encrypted data
        const combined = Uint8Array.from(atob(encryptedValue), c => c.charCodeAt(0));

        if (combined.length < 13) {
            throw new Error('Invalid encrypted data format');
        }

        const iv = combined.slice(0, 12);
        const encryptedData = combined.slice(12);

        const decryptedData = await window.crypto.subtle.decrypt(
            { name: 'AES-GCM', iv },
            key,
            encryptedData
        );

        const decoder = new TextDecoder();
        return decoder.decode(decryptedData);
    } catch (error) {
        console.error('Decryption error:', error);

        // Provide more helpful error messages
        if (error.name === 'OperationError') {
            throw new Error('Failed to decrypt data. Session may have expired.');
        }

        throw new Error('Failed to decrypt data: ' + error.message);
    }
};

/**
 * Clear encrypted data, session key, and cache
 */
export const clearEncryptedData = () => {
    sessionStorage.removeItem('_sk');
    sessionStorage.removeItem('tempPassword');

    // Clear key cache
    keyCache = null;
    keyCachePassword = null;
};

/**
 * Check if encryption is available in the current environment
 * @returns {boolean} - True if crypto operations are supported
 */
export const isCryptoAvailable = () => {
    return !!(window.crypto && window.crypto.subtle);
};
